/**
 * Lazy-load <video data-src="…"> elements.
 *
 * Rationale: the session page has 5 videos totalling ~38 MB. With
 * autoplay + preload="metadata" the browser fetches significant bytes
 * for every video on initial load — even the ones below the fold.
 *
 * This swaps in the real `src` only when a video enters the viewport
 * (with 200px of margin so loading starts just before it's visible),
 * then starts playback. Once loaded, the video stays attached and
 * autoplays loop silently.
 */
export function initLazyVideos(): void {
  const videos = document.querySelectorAll<HTMLVideoElement>("video[data-src]");
  if (videos.length === 0) return;

  // Fallback for browsers without IntersectionObserver: load everything.
  if (!("IntersectionObserver" in window)) {
    videos.forEach(promote);
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          promote(entry.target as HTMLVideoElement);
          io.unobserve(entry.target);
        }
      });
    },
    { rootMargin: "200px 0px", threshold: 0.01 },
  );

  videos.forEach((v) => io.observe(v));
}

function promote(video: HTMLVideoElement): void {
  const src = video.dataset.src;
  if (!src) return;
  video.src = src;
  video.removeAttribute("data-src");
  // playsinline + muted are already on the element; play() needs the user
  // gesture exception, which muted videos always have.
  video.play().catch(() => {
    /* user-agent blocked autoplay; nothing to do */
  });
}
